/**
 * REST calls for the 1:1 call feature. Every request goes through the shared
 * ApiClient singleton (JWT + CSRF + base URL). Endpoints and payloads mirror
 * app/Service/Api/Controller/Calls/*.php.
 *
 * IDs are strings everywhere (they exceed Number.MAX_SAFE_INTEGER). Errors are
 * normalised to CallApiError so callers can branch on the machine `code`
 * (call_authorization_required, call_busy, ...) rather than the localized text.
 */

/**
 * A failed call REST request. `code` is the backend machine code, `data`
 * carries any extra error fields (the blocking call for call_busy, the
 * authorization state for call_authorization_required).
 */
export class CallApiError extends Error {
    constructor(status, code, message, data) {
        super(message || code || 'call_request_failed');
        this.name = 'CallApiError';
        this.status = status;
        this.code = code || '';
        this.data = data || {};
    }
}

function client() {
    if (!window.ApiClient) {
        throw new CallApiError(0, 'client_unavailable', 'API client not ready');
    }
    return window.ApiClient;
}

/**
 * Parse a Response, returning the JSON body on success or throwing a
 * CallApiError shaped from the { error: { code, message, ... } } envelope.
 */
async function parse(response) {
    if (response.status === 204) {
        return null;
    }

    let body = null;
    try {
        body = await response.json();
    } catch (e) {
        body = null;
    }

    if (response.ok) {
        return body || {};
    }

    const err = (body && body.error) || {};
    // Extra fields (authorization, call, role) sit alongside code/message.
    const { type, code, message, ...rest } = err;
    throw new CallApiError(response.status, code, message, rest);
}

function form(fields) {
    const body = new URLSearchParams();
    Object.keys(fields).forEach((key) => {
        const value = fields[key];
        if (value !== undefined && value !== null) {
            body.append(key, String(value));
        }
    });
    return body;
}

async function send(endpoint, method, fields) {
    const options = { method };
    if (fields) {
        options.body = form(fields);
        options.headers = { 'Content-Type': 'application/x-www-form-urlencoded' };
    }
    return parse(await client().request(endpoint, options));
}

export const callApi = {
    // Lifecycle
    createCall: (targetId, type) => send('/call', 'POST', { target_id: String(targetId), type }),
    acceptCall: (callId) => send(`/call/${encodeURIComponent(String(callId))}/accept`, 'PUT'),
    declineCall: (callId) => send(`/call/${encodeURIComponent(String(callId))}/decline`, 'PUT'),
    cancelCall: (callId) => send(`/call/${encodeURIComponent(String(callId))}/cancel`, 'PUT'),
    endCall: (callId, reason) => send(`/call/${encodeURIComponent(String(callId))}/end`, 'PUT', { reason }),
    getCall: (callId) => send(`/call/${encodeURIComponent(String(callId))}`, 'GET'),
    getToken: (callId) => send(`/call/${encodeURIComponent(String(callId))}/token`, 'GET'),

    // Per-pair authorization handshake
    getAuthorization: (userId) => send(`/call-authorization/${encodeURIComponent(String(userId))}`, 'GET'),
    requestAuthorization: (targetId) => send('/call-authorization', 'POST', { target_id: String(targetId) }),
    acceptAuthorization: (userId) => send(`/call-authorization/${encodeURIComponent(String(userId))}/accept`, 'PUT'),
    declineAuthorization: (userId) => send(`/call-authorization/${encodeURIComponent(String(userId))}/decline`, 'PUT'),
    revokeAuthorization: (userId) => send(`/call-authorization/${encodeURIComponent(String(userId))}`, 'DELETE'),
};

export default callApi;
